/**
 * @file SessionsByDevice.js
 * @description Card for the Sessions By Device dashboard.
 */

import React, { useState, useEffect } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import NotEnoughData from './NotEnoughData';
import CustomDashboardTooltip from '../helpers/CustomDashboardTooltip';
import './SessionsByDevice.scss';
import { getText } from '../../i18n';

const mapApiResponseToSessionsByDeviceData = dataFromApi => {
    const counts = {
        Web: 0,
        Android: 0,
        iOS: 0,
        Total: 0,
    };

    dataFromApi.forEach(({ name, count }) => {
        switch (name) {
            case 'Web':
                counts.Web += count;
                break;
            case 'Android':
                counts.Android += count;
                break;
            case 'iOS':
                counts.iOS += count;
                break;
            case 'Total':
                counts.Total += count;
                break;
            default:
                break;
        }
    });

    return [
        {
            name: 'Desktop',
            count: counts.Web,
            remaining: Math.abs(counts.Total - counts.Web),
        },
        {
            name: 'Android',
            count: counts.Android,
            remaining: Math.abs(counts.Total - counts.Android),
        },
        {
            name: 'iOS',
            count: counts.iOS,
            remaining: Math.abs(counts.Total - counts.iOS),
        },
    ];
};

const SessionsByDevice = ({ sessionsByDeviceData }) => {
    const [chartData, setChartData] = useState([]);
    const [totalSessions, setTotalSessions] = useState(0);
    useEffect(() => {
        if (sessionsByDeviceData?.length > 0) {
            const mappedData =
                mapApiResponseToSessionsByDeviceData(sessionsByDeviceData);
            const allSessions = sessionsByDeviceData?.find(
                item => item?.name === 'Total',
            )?.count;
            setChartData(mappedData);
            setTotalSessions(allSessions);
        } else {
            setChartData([]);
            setTotalSessions(0);
        }
    }, [sessionsByDeviceData]);

    if (totalSessions === 0) {
        return <NotEnoughData />;
    }

    return (
        <div className="sessions-by-device-container">
            <div className="sessions-container">
                <div className="sessions-count">
                    {totalSessions.toLocaleString()}
                </div>
                <div className="sessions-text">{getText('msg_9fed2b674b94')}</div>
            </div>
            {chartData.map((item, index) => (
                <div key={index} className="device-session-row">
                    <div className="device-session-label">
                        <div className="device-session-item-name">
                            {item.name}
                        </div>
                        <div className="device-session-item-number">
                            {item.count.toLocaleString()}
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height={25}>
                        <BarChart
                            layout="vertical"
                            data={[item]}
                            margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                        >
                            <XAxis
                                type="number"
                                domain={[0, totalSessions]}
                                hide
                            />
                            <YAxis type="category" dataKey="name" hide />
                            <Tooltip
                                content={({ active, payload }) => {
                                    if (payload && payload.length > 0) {
                                        const { name, count } =
                                            payload[0].payload;
                                        return (
                                            <CustomDashboardTooltip
                                                active={active}
                                                label={name}
                                                value={count}
                                                unit="sessions"
                                            />
                                        );
                                    }
                                }}
                                cursor={false}
                            />
                            <Bar
                                dataKey="count"
                                stackId="a"
                                fill="#1890FF"
                                isAnimationActive={true}
                                barSize={15}
                            />
                            <Bar
                                dataKey="remaining"
                                stackId="a"
                                fill="#CCCCCC"
                                isAnimationActive={true}
                                barSize={15}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            ))}
        </div>
    );
};
export default SessionsByDevice;
